import { useMutation } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { MutationConfig, queryClient } from '@/src/lib/react-query/react-query';
import { generateRoutePath } from '@/src/routes';

import { UpdateEmailEntryResponse } from './config';
import { emailEntriesKeys } from './getEmailEntries';
import { emailEntryKeys } from './getEmailEntry';

import { isResponseError } from '../../../../api/config';
import { entryActionCustomValidateOptions } from '../../../../api/inbox';
import { InboxEntryStatus } from '../../../../types';

// const url = `${PREFIX_URL}/v01/inbox/status`;
const url = `${PREFIX_URL}/inbox-threads/:inboxEntryId/emails/:emailId`;

export type UpdateEmailStatusDTO = {
  projectId: string;
  inboxEntryId: number;
  emailId: number;
  status: InboxEntryStatus;
};

export const updateEmailStatus = ({
  projectId,
  inboxEntryId,
  emailId,
  status,
}: UpdateEmailStatusDTO): Promise<UpdateEmailEntryResponse> => {
  return axios.put(
    generateRoutePath(url, { projectId, inboxEntryId, emailId }),
    { status },
    {
      ...entryActionCustomValidateOptions,
    }
  );
};

type useUpdateEmailStatusOptions = {
  projectId: string;
  inboxEntryId: number;
  emailId: number;
  config?: MutationConfig<typeof updateEmailStatus>;
};

export const useUpdateEmailStatus = ({
  projectId,
  inboxEntryId,
  emailId,
  config,
}: useUpdateEmailStatusOptions) => {
  return useMutation({
    onSuccess: (data) => {
      if (isResponseError(data)) {
        return;
      }

      queryClient.setQueriesData(
        [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            pages: prev.pages.map((page) => {
              return {
                ...page,
                data: {
                  ...page.data,
                  emails: page.data.emails.map((emailEntry) => {
                    if (emailEntry.id === emailId) {
                      return {
                        ...emailEntry,
                        status: data.data['inbox-email'].status,
                      };
                    }
                    return emailEntry;
                  }),
                },
              };
            }),
          };
        }
      );

      if (
        !queryClient.getQueryData([...emailEntryKeys.detail({ emailId, inboxEntryId, projectId })])
      ) {
        return;
      }
      queryClient.setQueryData(
        [...emailEntryKeys.detail({ emailId, inboxEntryId, projectId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              status: data.data['inbox-email'].status,
              openedDate: data.data['inbox-email'].opened?.date,
              openedBy: data.data['inbox-email'].opened?.name,
              modifiedDate: data.data['inbox-email'].modified?.date,
              modifiedBy: data.data['inbox-email'].modified?.name,
              ...(data.data['inbox-email'].closed && {
                closedDate: data.data['inbox-email'].closed?.date,
                closedBy: data.data['inbox-email'].closed?.name,
              }),
            },
          };
        }
      );
    },
    ...config,
    mutationFn: updateEmailStatus,
  });
};
